import React from "react";

import TuneIcon from "@mui/icons-material/Tune";
import CloseIcon from "@mui/icons-material/Close";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

import { getServiceUri } from "@auto/metadata";
import { rest, t } from "@auto/services";

// Reuse the existing voter-list filter drawer design rather than adding tabs.
import styles from "@src/lists/VoterListFacets.module.styl";

const TYPES = ["CENTER", "BEC", "BED"];
const STATUSES = ["PENDING", "READY", "ERROR", "APPROVED", "REJECTED"];

export const DEFAULT_OPERATOR_REQUEST_FILTERS = Object.freeze({
  operatorType: "",
  requestStatus: []
});

export const normalizeOperatorRequestFilters = (value) => {
  const input = value && typeof value === "object" && !Array.isArray(value) ? value : {};
  const rawType = String(input.operatorType || "").trim().toUpperCase();
  const operatorType = TYPES.includes(rawType) ? rawType : "";
  const requestStatus = Array.isArray(input.requestStatus)
    ? [...new Set(input.requestStatus.map((status) => String(status).trim().toUpperCase()))]
      .filter((status) => STATUSES.includes(status))
    : [];

  return { operatorType, requestStatus };
};

export const isDefaultOperatorRequestFilters = (value) => {
  const filters = normalizeOperatorRequestFilters(value);
  return !filters.operatorType && filters.requestStatus.length === 0;
};

export const countOperatorRequestFilters = (value) => {
  const filters = normalizeOperatorRequestFilters(value);
  return (filters.operatorType ? 1 : 0) + filters.requestStatus.length;
};

const countFor = (counts, group, value) => {
  const count = Number(counts?.[group]?.[value]);
  return Number.isFinite(count) ? count : 0;
};

const Option = ({ label, count, selected, onClick, radio = false }) => (
  <button
    type="button"
    className={selected ? `${styles.option} ${styles.optionSelected}` : styles.option}
    onClick={onClick}
    aria-pressed={selected}
  >
    <span className={radio ? styles.radioDot : styles.checkBox}>{!radio && selected ? "✓" : ""}</span>
    <span className={styles.optionLabel}>{label}</span>
    <span className={styles.count}>{count}</span>
  </button>
);

const Chip = ({ label, onRemove }) => (
  <button type="button" className={styles.chip} onClick={onRemove}>
    <span>{label}</span>
    <span className={styles.chipClose}>×</span>
  </button>
);

const FilterButton = ({ controller }) => (
  <button
    type="button"
    className={controller.activeCount
      ? `${styles.filterButton} ${styles.filterButtonActive}`
      : styles.filterButton}
    onClick={controller.toggleDrawer}
  >
    <span className={styles.filterButtonIcon}><TuneIcon fontSize="inherit" /></span>
    <span className={styles.filterButtonLabel}>{t("common.filters")}</span>
    {controller.activeCount > 0 ? <span className={styles.filterButtonBadge}>{controller.activeCount}</span> : null}
  </button>
);

const FilterDrawer = ({ controller }) => {
  const filters = normalizeOperatorRequestFilters(controller.value);
  const statuses = filters.requestStatus;

  const toggleStatus = (status) => controller.patchValue({
    requestStatus: statuses.includes(status)
      ? statuses.filter((entry) => entry !== status)
      : [...statuses, status]
  });

  return (
    <div className={styles.drawer}>
      <div className={styles.drawerHeader}>
        <div className={styles.drawerTitle}>{t("common.filters")}</div>
        <button type="button" className={styles.closeButton}
          onClick={controller.closeDrawer} aria-label={t("common.closeFilters")}>
          <CloseIcon fontSize="inherit" />
        </button>
      </div>

      <div className={styles.drawerBody}>
        <section className={styles.section}>
          <div className={styles.sectionTitle}>{t("operator.type")}</div>
          <div className={styles.sectionBody}>
            {TYPES.map((role) => (
              <Option key={role} radio
                label={t(`operator.type.${role}`)}
                count={countFor(controller.counts, "operatorType", role)}
                selected={filters.operatorType === role}
                onClick={() => controller.patchValue({
                  operatorType: filters.operatorType === role ? "" : role
                })}
              />
            ))}
          </div>
        </section>

        <section className={styles.section}>
          <div className={styles.sectionTitle}>{t("user.requestStatus")}</div>
          <div className={styles.sectionBody}>
            {STATUSES.map((status) => (
              <Option key={status}
                label={t(status)}
                count={countFor(controller.counts, "status", status)}
                selected={statuses.includes(status)}
                onClick={() => toggleStatus(status)}
              />
            ))}
          </div>
        </section>
      </div>

      <div className={styles.drawerFooter}>
        <button type="button" className={styles.secondaryAction}
          disabled={!controller.activeCount} onClick={controller.reset}>
          <RestartAltIcon fontSize="inherit" />
          <span>{t("common.resetFilters")}</span>
        </button>
        <button type="button" className={styles.primaryAction} onClick={controller.closeDrawer}>
          {t("common.done")}
        </button>
      </div>
    </div>
  );
};

const FilterChips = ({ controller }) => {
  const filters = normalizeOperatorRequestFilters(controller.value);
  if (isDefaultOperatorRequestFilters(filters)) return null;

  return (
    <div className={styles.chipsBar}>
      {filters.operatorType ? (
        <Chip label={`${t("operator.type")}: ${t(`operator.type.${filters.operatorType}`)}`}
          onRemove={() => controller.patchValue({ operatorType: "" })} />
      ) : null}
      {filters.requestStatus.map((status) => (
        <Chip key={status} label={`${t("user.requestStatus")}: ${t(status)}`}
          onRemove={() => controller.patchValue({
            requestStatus: filters.requestStatus.filter((entry) => entry !== status)
          })} />
      ))}
      <button type="button" className={styles.clearAll} onClick={controller.reset}>
        {t("common.clearAll")}
      </button>
    </div>
  );
};

export const OPERATOR_REQUEST_FACETS = {
  storageKey: "security.operator-account-request.facets",
  overlayLabel: t("common.filters"),
  defaultValue: DEFAULT_OPERATOR_REQUEST_FILTERS,
  normalizeValue: normalizeOperatorRequestFilters,
  isDefaultValue: isDefaultOperatorRequestFilters,
  getActiveCount: countOperatorRequestFilters,
  loadCounts: ({ search, filters }) => rest.request(
    `${getServiceUri()}operator-account-request-list/facets`, "POST", {
      search: String(search || "").trim(),
      filters: normalizeOperatorRequestFilters(filters)
    }
  ),
  renderButton: (controller) => <FilterButton controller={controller} />,
  renderDrawer: (controller) => <FilterDrawer controller={controller} />,
  renderChips: (controller) => <FilterChips controller={controller} />
};
